import { useHideAuthorizations } from '@/utils/featureFlags.ts'
import type { RouteRecordRaw } from 'vue-router'

declare module 'vue-router' {
	// This augments the existing RouteMeta interface
	interface RouteMeta {
		requiresAuth?: boolean
		hideNavigation?: boolean
		hideFooter?: boolean
		hideUntilPlanSelected?: boolean
		showBasicTemplate?: boolean
		redirectOnly?: boolean
		url?: string
		title?: string
		pageTitle?: boolean
		reset?: boolean
		contractTypes?: ('medical' | 'dental' | 'vision')[]
		rightsValue?: RightsValue[]
	}
}

export const routes: RouteRecordRaw[] = [
	{
		path: '/',
		redirect: '/login',
		meta: {
			hideNavigation: true,
			hideHeader: true,
			hideFooter: true,
			showBasicTemplate: true,
			hideUntilPlanSelected: true
		}
	},
	{
		path: '/anytime',
		redirect:
			'/login?utm_source=vanity_url&utm_medium=audio&utm_campaign=on_hold_message&utm_content=anytime',
		meta: { hideNavigation: true, hideFooter: true, hideUntilPlanSelected: true }
	},
	{
		path: '/login',
		name: 'login',
		component: () => import('../views/LandingPage.vue'),
		meta: {
			hideNavigation: true,
			hideHeader: true,
			hideFooter: true,
			showBasicTemplate: true,
			hideUntilPlanSelected: true
		}
	},
	{
		path: '/initiate-login',
		name: 'InitiateLogin',
		component: () => import('../views/InitiateLogin.vue'),
		meta: { hideNavigation: true, hideFooter: true, hideUntilPlanSelected: true }
	},
	{
		path: '/register',
		name: 'VerifyMember',
		component: () => import('../views/registration/VerifyMember.vue'),
		meta: { hideNavigation: true, hideFooter: true, hideUntilPlanSelected: true }
	},
	{
		path: '/register/verify-information',
		name: 'VerifyInformation',
		component: () => import('../views/registration/VerifyInformation.vue'),
		meta: { hideNavigation: true, hideFooter: true, hideUntilPlanSelected: true }
	},
	{
		path: '/register/create-account',
		name: 'CreateAccount',
		component: () => import('../views/registration/CreateAccount.vue'),
		meta: { hideNavigation: true, hideFooter: true, hideUntilPlanSelected: true }
	},
	{
		path: '/register/communication-settings',
		name: 'CommunicationSettings',
		component: () => import('../views/registration/CommunicationSettings.vue'),
		meta: { hideNavigation: true, hideFooter: true, hideUntilPlanSelected: true }
	},
	{
		path: '/register/complete-registration',
		name: 'CompleteRegistration',
		component: () => import('../views/registration/CompleteRegistration.vue'),
		meta: { hideNavigation: true, hideFooter: true, hideUntilPlanSelected: true }
	},
	{
		path: '/bypass-outage',
		name: 'BypassOutage',
		redirect: '/login',
		meta: { hideNavigation: true, hideFooter: true, hideUntilPlanSelected: true }
	},
	{
		path: '/outage-message',
		name: 'outagemsg',
		component: () => import('../views/OutageMsg.vue'),
		meta: { hideNavigation: true, hideFooter: true, hideUntilPlanSelected: true }
	},
	{
		path: '/select-plan',
		name: 'SelectPlan',
		component: () => import('../views/SelectPlan.vue'),
		meta: {
			requiresAuth: true,
			hideFooter: true,
			hideUntilPlanSelected: true,
			title: 'Select Plan'
		}
	},
	{
		path: '/dashboard',
		redirect: '/medical/dashboard',
		meta: {
			requiresAuth: true
		}
	},
	{
		path: '/medical/dashboard',
		name: 'MemberDashboard',
		component: () => import('../views/MemberDashboard.vue'),
		meta: {
			requiresAuth: true
		}
	},
	{
		path: '/dv-dashboard',
		redirect: '/dental/dashboard',
		meta: {
			requiresAuth: true
		}
	},
	{
		path: '/dental/dashboard',
		name: 'DentalDashboard',
		component: () => import('../views/DentalVisionDashboard.vue'),
		alias: ['/dental/dashboard'],
		meta: {
			requiresAuth: true,
			contractTypes: ['dental']
		}
	},
	{
		path: '/vision/dashboard',
		name: 'VisionDashboard',
		component: () => import('../views/DentalVisionDashboard.vue'),
		alias: ['/vision/dashboard'],
		meta: {
			requiresAuth: true,
			contractTypes: ['vision']
		}
	},
	{
		path: '/spending-details',
		name: 'SpendingDetails',
		component: () => import('../views/SpendingDetails.vue'),
		alias: ['/medical/spending-details'],
		meta: {
			requiresAuth: true,
			title: 'Spending Details',
			contractTypes: ['medical'],
			rightsValue: ['deductible']
		}
	},
	{
		path: '/pharmacy-spending',
		redirect: '/medical/pharmacy-spending/1?clear=true'
	},
	{
		path: '/medical/pharmacy-spending',
		redirect: '/medical/pharmacy-spending/1?clear=true'
	},
	{
		path: '/pharmacy-spending/:page',
		name: 'WmPharmacySpending',
		component: () => import('../views/WmPharmacySpending.vue'),
		alias: ['/medical/pharmacy-spending/:page'],
		meta: {
			requiresAuth: true,
			title: 'Pharmacy Spending',
			contractTypes: ['medical'],
			rightsValue: ['wm-pharmacy-spending']
		}
	},
	{
		path: '/health-reimbursement',
		name: 'HealthReimbursement',
		component: () => import('../views/HealthReimbursement.vue'),
		alias: ['/medical/health-reimbursement'],
		meta: {
			requiresAuth: true,
			title: 'Health Reimbursement Account',
			contractTypes: ['medical'],
			rightsValue: ['healthReimbursementAccount']
		}
	},
	{
		path: '/programs',
		name: 'Programs',
		component: () => import('../views/Programs.vue'),
		alias: ['/medical/programs', '/dental/programs', '/vision/programs'],
		meta: {
			requiresAuth: true,
			title: 'Programs',
			contractTypes: ['medical', 'dental', 'vision'],
			rightsValue: ['programs']
		}
	},
	{
		path: '/programs/:id',
		name: 'ProgramDetails',
		component: () => import('../views/ProgramDetails.vue'),
		alias: ['/medical/programs/:id', '/dental/programs/:id', '/vision/programs/:id'],
		meta: {
			requiresAuth: true,
			pageTitle: true,
			contractTypes: ['medical', 'dental', 'vision'],
			rightsValue: ['programs-id']
		}
	},
	{
		path: '/pharmacy',
		name: 'Pharmacy',
		component: () => import('../views/pharmacy/Pharmacy.vue'),
		alias: ['/medical/pharmacy'],
		meta: {
			requiresAuth: true,
			title: 'Pharmacy',
			contractTypes: ['medical', 'dental', 'vision'],
			rightsValue: ['pharmacyCenter', 'rxSavingsSolutions']
		}
	},
	{
		path: '/pharmacy-costs',
		name: 'PharmacyCosts',
		component: () => import('../views/pharmacy/PharmacyCostsSearch.vue'),
		alias: ['/medical/pharmacy-costs'],
		meta: {
			requiresAuth: true,
			title: 'Pharmacy Costs',
			contractTypes: ['medical'],
			rightsValue: ['pharmacyCenter'],
			reset: false
		}
	},
	{
		path: '/pharmacy-costs/results',
		name: 'PharmacyCostsResults',
		component: () => import('../views/pharmacy/PharmacyCostsResults.vue'),
		alias: ['/medical/pharmacy-costs/results'],
		meta: {
			requiresAuth: true,
			title: 'Pharmacy Costs',
			contractTypes: ['medical'],
			rightsValue: ['pharmacyCenter'],
			reset: false
		}
	},
	{
		path: '/rx-prior-authorizations',
		name: 'PharmacyPriorAuths',
		component: () => import('../views/pharmacy/PharmacyPriorAuths.vue'),
		alias: ['/medical/rx-prior-authorizations'],
		meta: {
			requiresAuth: true,
			title: 'Prior Authorizations',
			contractTypes: ['medical'],
			rightsValue: ['pharmacyCenter'],
			reset: false
		}
	},
	{
		path: '/pharmacy-costs/results/:id',
		name: 'PharmacyPriceDetails',
		component: () => import('../views/pharmacy/PharmacyCostsDetails.vue'),
		alias: ['/medical/pharmacy-costs/results/:id'],
		meta: {
			requiresAuth: true,
			title: 'Pharmacy Costs',
			contractTypes: ['medical'],
			rightsValue: ['pharmacyCenter']
		}
	},
	{
		path: '/pharmacy-costs/pharmacy-list',
		name: 'PharmacyList',
		component: () => import('../views/pharmacy/PharmacyList.vue'),
		alias: ['/medical/pharmacy-costs/pharmacy-list'],
		meta: {
			requiresAuth: true,
			title: 'Pharmacy List',
			contractTypes: ['medical'],
			rightsValue: ['pharmacyCenter']
		}
	},
	{
		path: '/contact-us',
		name: 'ContactUs',
		component: () => import('../views/ContactUs.vue'),
		alias: ['/medical/contact-us', '/dental/contact-us', '/vision/contact-us'],
		meta: {
			requiresAuth: true,
			title: 'Contact Us',
			contractTypes: ['medical', 'dental', 'vision']
		}
	},
	{
		path: '/contact',
		name: 'ContactUnauth',
		component: () => import('../views/public/ContactUs.vue'),
		meta: {
			title: 'Contact Us',
			hideNavigation: true,
			hideFooter: true,
			showBasicTemplate: true,
			hideUntilPlanSelected: true
		}
	},
	{
		path: '/medical-claims',
		redirect: '/claims',
		meta: { requiresAuth: true }
	},
	{
		path: '/medical/medical-claims',
		redirect: '/claims',
		meta: { requiresAuth: true }
	},
	{
		path: '/claims',
		name: 'MedicalClaims',
		component: () => import('../views/MedicalClaims.vue'),
		alias: ['/medical/claims'],
		meta: {
			requiresAuth: true,
			title: 'Claims',
			contractTypes: ['medical'],
			rightsValue: ['claims']
		}
	},
	{
		path: '/claim-details/:id',
		name: 'ClaimDetails',
		component: () => import('../views/ClaimDetails.vue'),
		alias: ['/medical/claim-details/:id'],
		meta: {
			requiresAuth: true,
			title: 'Claim Details',
			contractTypes: ['medical'],
			rightsValue: ['claims']
		}
	},
	{
		path: '/eobs',
		alias: ['/medical/eobs'],
		name: 'ExplanationOfBenefitsRedirect',
		redirect: { name: 'ExplanationOfBenefits', params: { page: 1 } }
	},
	{
		path: '/eobs/:page',
		name: 'ExplanationOfBenefits',
		component: () => import('../views/ExplanationOfBenefits.vue'),
		alias: ['/medical/eobs/:page'],
		meta: {
			requiresAuth: true,
			title: 'Explanation of Benefits',
			contractTypes: ['medical'],
			rightsValue: ['claims']
		}
	},
	{
		path: '/payment-center',
		name: 'PaymentCenter',
		component: () => import('../views/PaymentCenter.vue'),
		alias: ['/medical/payment-center', '/dental/payment-center', '/vision/payment-center'],
		meta: {
			requiresAuth: true,
			title: 'Payment Center',
			contractTypes: ['medical', 'dental', 'vision'],
			rightsValue: ['payment']
		}
	},
	{
		path: '/primary-care-provider',
		name: 'PrimaryCareProvider',
		component: () => import('../views/PrimaryCareProvider.vue'),
		alias: ['/medical/primary-care-provider'],
		meta: {
			requiresAuth: true,
			title: 'Primary Care Provider',
			contractTypes: ['medical'],
			rightsValue: ['choosePrimaryCareProvider']
		}
	},
	{
		path: '/coverage-and-copays',
		name: 'CoverageAndCopays',
		component: () => import('../views/CoverageAndCopays.vue'),
		alias: ['/medical/coverage-and-copays', '/vision/coverage-and-copays'],
		meta: {
			requiresAuth: true,
			title: 'Coverage and Copays',
			contractTypes: ['medical'],
			rightsValue: ['whosCovered']
		}
	},
	{
		path: '/dental/coverage-and-benefits',
		name: 'CoverageAndBenefits',
		component: () => import('../views/CoverageAndCopays.vue'),
		alias: ['/coverage-and-benefits'],
		meta: {
			requiresAuth: true,
			title: 'Coverage and Benefits',
			contractTypes: ['dental'],
			rightsValue: ['whosCovered']
		}
	},
	{
		path: '/vision/coverage',
		name: 'CoverageVision',
		component: () => import('../views/CoverageAndCopays.vue'),
		alias: ['/coverage'],
		meta: {
			requiresAuth: true,
			title: 'Coverage',
			contractTypes: ['vision'],
			rightsValue: ['whosCovered']
		}
	},
	{
		path: '/prior-authorizations',
		redirect: { name: 'AuthorizationsListing', params: { page: 1 } },
		alias: ['/medical/prior-authorizations'],
		meta: {
			requiresAuth: true,
			title: 'Authorizations',
			contractTypes: ['medical'],
			rightsValue: ['inpatientMedicalAdmissionRequest']
		}
	},
	{
		path: '/authorizations',
		// bare URL goes to page 1 of the listing
		redirect: { name: 'AuthorizationsListing', params: { page: 1 } },
		alias: ['/medical/authorizations'],
		meta: { requiresAuth: true }
	},
	{
		path: '/authorizations/:page(\\d+)',
		name: 'AuthorizationsListing',
		component: () =>
			useHideAuthorizations()
				? import('../views/AuthorizationsMaintenance.vue')
				: import('../views/AuthorizationsListing.vue'),
		alias: ['/medical/authorizations/:page(\\d+)'],
		meta: {
			requiresAuth: true,
			title: 'Authorizations',
			contractTypes: ['medical'],
			rightsValue: ['inpatientMedicalAdmissionRequest']
		}
	},
	{
		path: '/authorizations/detail/:id',
		name: 'AuthorizationsDetails',
		component: () => import('../views/AuthorizationsDetails.vue'),
		beforeEnter: () => (useHideAuthorizations() ? { name: 'AuthorizationsListing' } : true),
		alias: ['/medical/authorizations/detail/:id'],
		meta: {
			requiresAuth: true,
			title: 'Prior Authorizations',
			contractTypes: ['medical'],
			rightsValue: ['inpatientMedicalAdmissionRequest']
		}
	},
	{
		path: '/find-care',
		name: 'FindCare',
		component: () => import('../views/FindCare.vue'),
		alias: ['/medical/find-care', '/dental/find-care', '/vision/find-care'],
		meta: {
			requiresAuth: true,
			title: 'Find Care',
			contractTypes: ['medical', 'dental', 'vision'],
			rightsValue: ['findCareAndCostOptions']
		}
	},
	{
		path: '/health-records',
		name: 'HealthRecords',
		component: () => import('../views/PersonalHealthRecords.vue'),
		alias: ['/medical/health-records'],
		meta: {
			requiresAuth: true,
			title: 'Health Records',
			contractTypes: ['medical'],
			rightsValue: ['personalHealthRecord']
		}
	},
	{
		path: '/traveling-abroad',
		name: 'CoverageAbroad',
		component: () => import('../views/CoverageAbroad.vue'),
		alias: ['/medical/traveling-abroad'],
		meta: {
			requiresAuth: true,
			title: 'Coverage Abroad',
			contractTypes: ['medical'],
			rightsValue: ['coverageWhenTravelingAbroad']
		}
	},
	{
		path: '/settings',
		redirect: '/settings/profile',
		alias: ['/medical/settings', '/dental/settings', '/vision/settings'],
		meta: {
			requiresAuth: true,
			title: 'Settings',
			contractTypes: ['medical', 'dental', 'vision']
		}
	},
	{
		path: '/settings/profile',
		name: 'SettingsProfile',
		component: () => import('../views/SettingsProfile.vue'),
		alias: ['/medical/settings/profile', '/dental/settings/profile', '/vision/settings/profile'],
		meta: {
			requiresAuth: true,
			title: 'Settings - Profile',
			contractTypes: ['medical', 'dental', 'vision']
		}
	},
	{
		path: '/settings/access',
		name: 'SettingsAccess',
		component: () => import('../views/SettingsAccess.vue'),
		alias: ['/medical/settings/access', '/dental/settings/access', '/vision/settings/access'],
		meta: {
			requiresAuth: true,
			title: 'Settings - Access',
			contractTypes: ['medical', 'dental', 'vision']
		}
	},
	{
		path: '/settings/privacy-security',
		name: 'SettingsPrivacy',
		component: () => import('../views/SettingsPrivacySecurity.vue'),
		alias: [
			'/medical/settings/privacy-security',
			'/dental/settings/privacy-security',
			'/vision/settings/privacy-security'
		],
		meta: {
			requiresAuth: true,
			title: 'Settings - Privacy & Security',
			contractTypes: ['medical', 'dental', 'vision']
		}
	},
	{
		path: '/settings/preferences',
		name: 'SettingsPreferences',
		component: () => import('../views/SettingsPreferences.vue'),
		alias: [
			'/medical/settings/preferences',
			'/dental/settings/preferences',
			'/vision/settings/preferences'
		],
		meta: {
			requiresAuth: true,
			title: 'Settings - Preferences',
			contractTypes: ['medical', 'dental', 'vision']
		}
	},
	{
		path: '/settings/about',
		name: 'SettingsAbout',
		component: () => import('../views/SettingsAbout.vue'),
		alias: ['/medical/settings/about', '/dental/settings/about', '/vision/settings/about'],
		meta: {
			requiresAuth: true,
			title: 'Settings - About',
			contractTypes: ['medical', 'dental', 'vision']
		}
	},
	{
		path: '/terms-of-use',
		name: 'TermsOfUse',
		component: () => import('../views/public/TermsOfUse.vue'),
		alias: ['/medical/terms-of-use', '/dental/terms-of-use', '/vision/terms-of-use'],
		meta: {
			// requiresAuth: true,
			showBasicTemplate: true,
			title: 'Terms of Use',
			contractTypes: ['medical', 'dental', 'vision']
		}
	},
	{
		path: '/account-help',
		name: 'HelpAccount',
		component: () => import('../views/public/HelpAccount.vue'),
		meta: {
			hideNavigation: true,
			hideFooter: true,
			showBasicTemplate: true,
			hideUntilPlanSelected: true,
			title: 'Need help with your account?'
		}
	},
	{
		path: '/brand-resources',
		name: 'BrandResources',
		component: () => import('../views/public/BrandResources.vue'),
		meta: {
			hideNavigation: true,
			hideFooter: true,
			showBasicTemplate: true,
			hideUntilPlanSelected: true,
			title: 'Brand Resources'
		}
	},
	{
		path: '/linking-disclaimer',
		name: 'LinkingDisclaimer',
		component: () => import('../views/LinkingDisclaimer.vue'),
		alias: [
			'/medical/linking-disclaimer',
			'/dental/linking-disclaimer',
			'/vision/linking-disclaimer'
		],
		meta: {
			requiresAuth: true,
			title: 'Linking Disclaimer',
			contractTypes: ['medical', 'dental', 'vision']
		}
	},
	{
		path: '/id-card',
		name: 'IdCard',
		component: () => import('../views/IdCard.vue'),
		alias: ['/medical/id-card', '/dental/id-card', '/vision/id-card'],
		meta: {
			requiresAuth: true,
			title: 'Member ID Card',
			contractTypes: ['medical', 'dental', 'vision'],
			rightsValue: ['idCards']
		}
	},
	{
		path: '/documents',
		name: 'DocumentsAndForms',
		component: () => import('../views/DocumentsAndForms.vue'),
		alias: ['/medical/documents', '/dental/documents', '/vision/documents'],
		meta: {
			requiresAuth: true,
			title: 'Documents',
			contractTypes: ['medical', 'dental', 'vision'],
			rightsValue: ['forms']
		}
	},
	{
		path: '/preventive-wellness',
		name: 'PreventiveWellness',
		component: () => import('../views/PreventiveWellness.vue'),
		alias: ['/medical/preventive-wellness'],
		meta: {
			requiresAuth: true,
			title: 'Preventive Wellness Services',
			contractTypes: ['medical'],
			rightsValue: ['preventativeCare']
		}
	},
	{
		path: '/linking',
		name: 'DeepLink',
		component: () => import('../views/DeepLink.vue'),
		alias: ['/medical/linking', '/dental/linking', '/vision/linking'],
		meta: {
			requiresAuth: true,
			title: 'Linking',
			contractTypes: ['medical', 'dental', 'vision']
		}
	},
	{
		path: '/cost-estimates',
		name: 'DeepLinkEmbold',
		component: () => import('../views/DeepLinkEmbold.vue'),
		alias: ['/medical/cost-estimates'],
		meta: {
			requiresAuth: true,
			title: 'Estimate Your Costs',
			contractTypes: ['medical'],
			rightsValue: ['embold']
		}
	},
	{
		path: '/message_center/:messageId',
		alias: ['/message_center'],
		redirect: (to) => {
			return { path: `/message/${to.params.messageId}` }
		},
		meta: {
			requiresAuth: true
		}
	},
	{
		path: '/messages',
		name: 'MessageCenter',
		component: () => import('../views/MessageCenter.vue'),
		alias: ['/medical/messages', '/dental/messages', '/vision/messages'],
		meta: {
			requiresAuth: true,
			title: 'Message Center',
			contractTypes: ['medical', 'dental', 'vision']
		}
	},
	{
		path: '/messages/:page',
		name: 'MessageCenterPage',
		component: () => import('../views/MessageCenter.vue'),
		alias: ['/medical/messages/:page', '/dental/messages/:page', '/vision/messages/:page'],
		meta: {
			requiresAuth: true,
			title: 'Message Center',
			contractTypes: ['medical', 'dental', 'vision']
		}
	},
	{
		path: '/message/:messageId',
		name: 'MessageCenterDetails',
		component: () => import('../views/MessageCenterDetails.vue'),
		alias: [
			'/medical/message/:messageId',
			'/dental/message/:messageId',
			'/vision/message/:messageId'
		],
		meta: {
			requiresAuth: true,
			title: 'Message Center',
			contractTypes: ['medical', 'dental', 'vision']
		}
	},
	{
		path: '/auth-success',
		name: 'AuthSuccess',
		component: () => import('../views/AuthSuccess.vue'),
		meta: {
			requiresAuth: true,
			hideNavigation: true,
			hideHeader: true,
			hideFooter: true,
			showBasicTemplate: true,
			hideUntilPlanSelected: true,
			title: 'Authentication'
		}
	},
	// External redirects
	{
		path: '/paperless',
		name: 'ExternalRedirects',
		component: () => import('../views/ExternalRedirects.vue'),
		meta: {
			hideNavigation: true,
			hideFooter: true,
			hideUntilPlanSelected: true,
			redirectOnly: true,
			url: 'https://info.blueprintportal.com/paperless'
		}
	},
	// This must be the last item or items after it will result in a 404
	{
		path: '/:pathMatch(.*)*',
		name: 'NotFound',
		component: () => import('../views/NotFound.vue'),
		meta: { hideNavigation: true, hideFooter: true, hideUntilPlanSelected: true }
	}
]
